import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, getDoc, getDocs, increment, where } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Heart, MessageCircle, Share2, PlayCircle, BookOpen, Search, LayoutGrid, BookMarked, Trash2, RotateCcw, Shapes } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toArray } from '../lib/utils';
import { OrganisationMember, Enrollment } from '../types';

interface Course {
  id: string;
  tutorId: string;
  tutorName: string;
  title: string;
  description: string;
  price: number;
  visibility?: 'private' | 'organisation' | 'public';
  orgId?: string;
  likesCount: number;
  commentsCount: number;
  createdAt: any;
  levels?: {
    explorer: string[];
    apprentice: string[];
    master: string[];
  };
}

export function Marketplace() {
  const { user, memberships, currentOrg } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Record<string, Enrollment>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [likedCourses, setLikedCourses] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'marketplace' | 'organisation' | 'my-courses'>('marketplace');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    let q;
    if (activeTab === 'marketplace') {
      q = query(
        collection(db, 'courses'), 
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc')
      );
    } else if (activeTab === 'organisation') {
      if (memberships.length === 0) {
        setCourses([]);
        return;
      }
      const orgIds = toArray<OrganisationMember>(memberships).map(m => m.orgId);
      // Firestore 'in' limit is 10. If more, we'd need a different strategy.
      q = query(
        collection(db, 'courses'), 
        where('visibility', '==', 'organisation'),
        where('orgId', 'in', orgIds),
        orderBy('createdAt', 'desc')
      );
    } else {
      // For my-courses tab, we'll fetch all courses but filter in frontend based on enrollments 
      // OR we can fetch them specifically. Let's just fetch recent courses for now and filter.
      // Better: fetch all public and organisation courses the user can see.
      q = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
      setCourses(coursesData);
    }, (error) => {
      // If it fails with insufficient permissions, it might be the broad query in my-courses tab
      if (activeTab === 'my-courses') {
         // Fallback: only fetch public and then maybe we need a dedicated "enrolled courses" fetcher
         // For now let's just ignore the error if it's permission related and we're in my-courses
         console.warn("Permission restricted query in my-courses tab, filtering logic might need adjustment.");
      }
      handleFirestoreError(error, OperationType.LIST, 'courses');
    });

    return () => unsubscribe();
  }, [activeTab, memberships]);

  useEffect(() => {
    if (!user) return;
    
    // Fetch user's enrollments
    const enrollmentsQuery = query(collection(db, 'enrollments'), where('userId', '==', user.uid));
    const unsubscribeEnrollments = onSnapshot(enrollmentsQuery, (snapshot) => {
      const enrollmentsData: Record<string, Enrollment> = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data() as Enrollment;
        enrollmentsData[data.courseId] = data;
      });
      setEnrollments(enrollmentsData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'enrollments');
    });

    // Fetch likes
    const fetchLikes = async () => {
      try {
        const q = query(collection(db, 'likes'), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        const likes = new Set<string>();
        snapshot.docs.forEach(doc => {
          likes.add(doc.data().courseId);
        });
        setLikedCourses(likes);
      } catch (error) {
        handleFirestoreError(error, OperationType.LIST, 'likes');
      }
    };
    fetchLikes();

    return () => unsubscribeEnrollments();
  }, [user]);

  const handleLike = async (courseId: string, currentLikes: number) => {
    if (!user) return;
    const likeId = `${user.uid}_${courseId}`;
    const likeRef = doc(db, 'likes', likeId);
    const courseRef = doc(db, 'courses', courseId);
    
    try {
      if (likedCourses.has(courseId)) {
        // Unlike
        await deleteDoc(likeRef);
        await setDoc(courseRef, { likesCount: increment(-1) }, { merge: true });
        setLikedCourses(prev => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
      } else {
        // Like
        await setDoc(likeRef, {
          userId: user.uid,
          courseId: courseId,
          createdAt: serverTimestamp()
        });
        await setDoc(courseRef, { likesCount: increment(1) }, { merge: true });
        setLikedCourses(prev => {
          const next = new Set(prev);
          next.add(courseId);
          return next;
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `likes/${likeId}`);
    }
  };

  const handleShare = (courseId: string, courseTitle: string, platform: 'whatsapp' | 'linkedin' | 'x') => {
    const url = `${window.location.origin}/course/${courseId}`;
    if (platform === 'whatsapp') {
      const text = encodeURIComponent(`Check out this course: ${courseTitle} ${url}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    } else if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    } else if (platform === 'x') {
      const text = encodeURIComponent(`Check out this course: ${courseTitle}`);
      window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
    }
  };

  const handleRemoveCourse = (courseId: string) => {
    if (!user) return;
    setConfirmModal({
      isOpen: true,
      title: 'Remove Course',
      message: 'Are you sure you want to remove this course from your list? Your progress will be lost.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'enrollments', `${user.uid}_${courseId}`));
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `enrollments/${user.uid}_${courseId}`);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleRestartCourse = (courseId: string) => {
    if (!user) return;
    setConfirmModal({
      isOpen: true,
      title: 'Restart Course',
      message: 'Are you sure you want to restart this course? Your current progress will be reset.',
      onConfirm: async () => {
        try {
          await setDoc(doc(db, 'enrollments', `${user.uid}_${courseId}`), {
            userId: user.uid,
            currentLevel: 'explorer',
            currentLessonIndex: 0,
            completed: false,
            lastAccessed: serverTimestamp()
          }, { merge: true });
        } catch (error) {
          handleFirestoreError(error, OperationType.UPDATE, `enrollments/${user.uid}_${courseId}`);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const calculateProgress = (course: Course, enrollment?: Enrollment) => {
    if (!enrollment || !course.levels) return 0;
    if (enrollment.completed) return 100;

    const levels = ['explorer', 'apprentice', 'master'] as const;
    let totalLessons = 0;
    let completedLessons = 0;

    for (const level of levels) {
      const levelData = (course.levels as any)[level] || [];
      const levelLessons = Array.isArray(levelData) ? levelData : (levelData.lessons || []);
      totalLessons += levelLessons.length;
      
      if (level === enrollment.currentLevel) {
        completedLessons += enrollment.currentLessonIndex;
      } else if (levels.indexOf(level as any) < levels.indexOf(enrollment.currentLevel as any)) {
        completedLessons += levelLessons.length;
      }
    }

    if (totalLessons === 0) return 0;
    return Math.round((completedLessons / totalLessons) * 100);
  };

  const filteredCourses = toArray<Course>(courses).filter(c => {
    const matchesSearch = (c.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (c.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (c.tutorName || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Quality Guards for Marketplace
    if (activeTab === 'marketplace') {
      const hasValidTitle = c.title && c.title.trim().length > 3 && !c.title.toLowerCase().includes('untitled');
      const explorerData = c.levels?.explorer;
      const explorerLessons = explorerData ? (Array.isArray(explorerData) ? explorerData : (explorerData as any).lessons || []) : [];
      const hasLessons = explorerLessons.length > 0;
      // We assume records in "courses" are published, but just to be safe:
      const notFailed = (c as any).status !== 'failed' && (c as any).status !== 'draft';
      const isPublic = c.visibility === 'public';
      
      if (!hasValidTitle || !hasLessons || !notFailed || !isPublic) return false;
    }

    if (activeTab === 'my-courses') {
      return matchesSearch && enrollments[c.id];
    }
    return matchesSearch;
  });

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-bg-main">
      {/* Sidebar */}
      <div className="w-64 bg-bg-alt border-r border-border-main p-6 hidden md:block shrink-0">
        <div className="space-y-2">
          <button
            onClick={() => setActiveTab('marketplace')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'marketplace' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
                : 'text-text-alt hover:bg-bg-main/50 hover:text-text-main'
            }`}
          >
            <LayoutGrid className="h-5 w-5" />
            Course Library
          </button>
          
          {memberships.length > 0 && (
            <button
              onClick={() => setActiveTab('organisation')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
                activeTab === 'organisation' 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
                  : 'text-text-alt hover:bg-bg-main/50 hover:text-text-main'
              }`}
            >
              <Shapes className="h-5 w-5" />
              Organisation
            </button>
          )}

          <button
            onClick={() => setActiveTab('my-courses')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'my-courses' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
                : 'text-text-alt hover:bg-bg-main/50 hover:text-text-main'
            }`}
          >
            <BookMarked className="h-5 w-5" />
            My Courses
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto">
        {/* Mobile Navigation */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('marketplace')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'marketplace' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-bg-alt text-text-alt border border-border-main'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Course Library
          </button>
          
          {memberships.length > 0 && (
            <button
              onClick={() => setActiveTab('organisation')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === 'organisation' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-bg-alt text-text-alt border border-border-main'
              }`}
            >
              <Shapes className="h-4 w-4" />
              Organisation
            </button>
          )}

          <button
            onClick={() => setActiveTab('my-courses')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'my-courses' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-bg-alt text-text-alt border border-border-main'
            }`}
          >
            <BookMarked className="h-4 w-4" />
            My Courses
          </button>
        </div>

        <div className="flex flex-col mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-3xl font-bold text-text-main tracking-tight">
              {activeTab === 'marketplace' ? 'Course Library' : activeTab === 'organisation' ? 'Organisation Library' : 'My Learning'}
            </h1>
            <div className="relative w-full sm:w-72">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-text-alt opacity-50" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-border-main rounded-lg leading-5 bg-bg-alt text-text-main placeholder-text-alt/50 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {activeTab === 'marketplace' && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border-main/30">
              <span className="text-xs text-text-alt flex items-center mr-2">I am interested in:</span>
              {['Leadership', 'Coaching', 'Career Growth', 'Business Strategy', 'Communication', 'Technology'].map(tag => (
                <button
                  key={tag}
                  onClick={() => setSearchTerm(searchTerm === tag ? '' : tag)}
                  className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors ${
                    searchTerm === tag ? 'bg-indigo-600 text-white' : 'bg-bg-alt text-text-alt hover:bg-bg-main hover:text-text-main border border-border-main'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {filteredCourses.map((course) => {
            const enrollment = enrollments[course.id];
            const progress = calculateProgress(course, enrollment);

            return (
              <div key={course.id} className="bg-bg-alt rounded-xl overflow-hidden shadow-lg border border-border-main flex flex-col transition-transform hover:-translate-y-1 duration-200">
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-semibold text-text-main line-clamp-2 pr-2">{course.title}</h3>
                    {activeTab === 'marketplace' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shrink-0">
                        Free course
                      </span>
                    )}
                    {activeTab === 'my-courses' && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button 
                          onClick={() => handleRestartCourse(course.id)}
                          className="p-1.5 text-text-alt hover:text-indigo-400 hover:bg-bg-main/50 rounded-lg transition-colors"
                          title="Restart Course"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleRemoveCourse(course.id)}
                          className="p-1.5 text-text-alt hover:text-red-400 hover:bg-bg-main/50 rounded-lg transition-colors"
                          title="Remove Course"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-text-alt mb-4 line-clamp-3">{course.description}</p>
                  <div className="flex items-center gap-2 text-xs text-text-alt/60 mb-4">
                    <BookOpen className="h-4 w-4" />
                    <span>By {course.tutorName || 'Unknown Tutor'}</span>
                  </div>

                  {activeTab === 'my-courses' && enrollment && (
                    <div className="mt-auto pt-4 border-t border-border-main/50">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-text-alt">Lesson Progress</span>
                        <span className="text-indigo-400 font-medium">{enrollment.lessonProgressPercentage ?? progress}%</span>
                      </div>
                      <div className="w-full bg-bg-main rounded-full h-2 overflow-hidden mb-3">
                        <div 
                          className="bg-indigo-500 h-2 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${enrollment.lessonProgressPercentage ?? progress}%` }}
                        />
                      </div>
                      
                      <div className="text-xs text-text-alt/80 space-y-1.5">
                        <div className="flex justify-between">
                          <span>Current Lesson:</span>
                          <span className="text-text-main capitalize">{enrollment.currentLevel} - L{enrollment.currentLessonIndex + 1}</span>
                        </div>
                        {enrollment.knowledgeCheckCompletedLessonIds && (
                          <div className="flex justify-between">
                            <span>Knowledge Checks:</span>
                            <span className="text-text-main">{enrollment.knowledgeCheckCompletedLessonIds.length} completed</span>
                          </div>
                        )}
                        {enrollment.lastAccessed && (
                          <div className="flex justify-between">
                            <span>Last Active:</span>
                            <span className="text-text-main">{new Date(enrollment.lastAccessed.toDate ? enrollment.lastAccessed.toDate() : enrollment.lastAccessed).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="px-6 py-4 bg-bg-main/20 border-t border-border-main flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => handleLike(course.id, course.likesCount)}
                      className={`flex items-center gap-1.5 text-sm transition-colors ${likedCourses.has(course.id) ? 'text-pink-500' : 'text-text-alt hover:text-pink-400'}`}
                    >
                      <Heart className={`h-5 w-5 ${likedCourses.has(course.id) ? 'fill-current' : ''}`} />
                      <span>{course.likesCount}</span>
                    </button>
                    <Link to={`/course/${course.id}`} className="flex items-center gap-1.5 text-sm text-text-alt hover:text-text-main transition-colors">
                      <MessageCircle className="h-5 w-5" />
                      <span>{course.commentsCount}</span>
                    </Link>
                    <div className="relative group">
                      <button className="flex items-center gap-1.5 text-sm text-text-alt hover:text-text-main transition-colors">
                        <Share2 className="h-5 w-5" />
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 pb-2 hidden group-hover:block z-10">
                        <div className="flex flex-col bg-bg-alt rounded-lg shadow-xl border border-border-main overflow-hidden">
                          <button onClick={() => handleShare(course.id, course.title, 'whatsapp')} className="px-4 py-2 text-sm text-text-main hover:bg-bg-main/50 text-left whitespace-nowrap">WhatsApp</button>
                          <button onClick={() => handleShare(course.id, course.title, 'linkedin')} className="px-4 py-2 text-sm text-text-main hover:bg-bg-main/50 text-left whitespace-nowrap">LinkedIn</button>
                          <button onClick={() => handleShare(course.id, course.title, 'x')} className="px-4 py-2 text-sm text-text-main hover:bg-bg-main/50 text-left whitespace-nowrap">X (Twitter)</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/course/${course.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <PlayCircle className="h-4 w-4" />
                    {activeTab === 'my-courses' ? (enrollment?.completed ? 'Review' : 'Continue') : 'Start'}
                  </Link>
                </div>
              </div>
            );
          })}
          {filteredCourses.length === 0 && (
            <div className="col-span-full py-12 text-center text-text-alt">
              <BookOpen className="mx-auto h-12 w-12 opacity-20 mb-4" />
              <p className="text-lg">
                {activeTab === 'my-courses' 
                  ? "You haven't started any courses yet." 
                  : (activeTab === 'organisation' 
                      ? (memberships.length === 0 ? "You are not currently assigned to an organisation." : "No organisation courses found.")
                      : "No public courses yet.")}
              </p>
              {activeTab === 'marketplace' && filteredCourses.length === 0 && (
                <p className="text-sm mt-2 opacity-70 cursor-default">When tutors publish public courses, they will appear here.</p>
              )}
              {activeTab === 'my-courses' && (
                <button 
                  onClick={() => setActiveTab('marketplace')}
                  className="mt-4 text-indigo-400 hover:text-indigo-300 font-medium"
                >
                  Browse Course Library
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-bg-alt rounded-2xl shadow-xl border border-border-main w-full max-w-md overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-bold text-text-main mb-2">{confirmModal.title}</h3>
              <p className="text-text-alt">{confirmModal.message}</p>
            </div>
            <div className="px-6 py-4 bg-bg-main/50 border-t border-border-main flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-sm font-medium text-text-alt hover:text-text-main transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
